'use client';

import { useState } from 'react';
import Image from 'next/image';
import { PortableText } from '@portabletext/react';

import { GroqImage } from '@/helpers/Image';
import type { PagePatrickAffiliateSchema } from '@/sanity/pages/PagePatrickAffiliateTemplate';
import { measuredRichText } from './richtext';

import statue from '@/public/fabio-affiliate/statue-card.png';
import dotCards from '@/public/fabio-affiliate/dots-card.png';
import logoAlt from '@/public/fabio-affiliate/logo-alt.svg';

/**
 * "Why Traders Choose IQ Capital" — Figma 8841:1865 (desktop) / 8811:2946
 * (mobile). Desktop is three 364px columns: labels, the dark IQ Capital card
 * with the statue, and the faint "Most Prop Firms" card. Mobile collapses to a
 * two-tab toggle over one list with dotted leaders.
 */
export default function ComparisonSection({
  data,
}: {
  data: PagePatrickAffiliateSchema;
}) {
  const [selectedTab, setSelectedTab] = useState<'iq' | 'other'>('iq');

  if (
    !data?.comparisonChip &&
    !data?.comparisonHeading?.length &&
    !data?.comparisonColumns?.length &&
    !data?.comparisonRows?.length
  )
    return null;

  const columns = data?.comparisonColumns ?? [];
  const rows = data?.comparisonRows ?? [];
  const cards = [
    {
      type: 'features' as const,
      column: columns[0],
      bgClass: 'bg-white',
      titleClass: 'text-black',
      textClass: 'text-black/50 uppercase font-[500] text-[2rem]',
      dividerClass: 'border-black/10',
    },
    {
      type: 'iq' as const,
      column: columns[1],
      bgClass: 'bg-[#112E27]',
      titleClass: 'text-white',
      textClass: 'text-white font-[400] text-[2.25rem]',
      dividerClass: 'border-white/10',
    },
    {
      type: 'other' as const,
      column: columns[2],
      bgClass: 'bg-[#112E27]/[8%]',
      titleClass: 'text-black',
      textClass: 'text-black font-[400] text-[2.25rem]',
      dividerClass: 'border-[#112E27]/10',
    },
  ];

  const title = (card: (typeof cards)[number], logoClass = '') =>
    card.column?.logo ? (
      <GroqImage
        src={card.column.logo}
        alt={card.column.label ?? ''}
        style={{ width: '15.738rem' }}
        className={`!w-[15.738rem] h-auto ${logoClass}`}
      />
    ) : (
      card.column?.label && (
        <span className="font-geist text-[3rem] font-[600] leading-[1.12]">
          {card.column.label}
        </span>
      )
    );

  return (
    <section className="bg-white px-[2rem] py-[8rem] lg:px-0 lg:pt-[15rem] lg:pb-[22.5rem]">
      <div className="mx-auto w-[min(140rem,100%)]">
        <div className="flex flex-col items-center text-center mb-[4rem] gap-[1.25rem]">
          {data?.comparisonChip && (
            <p className="m-0 italic text-black font-blacker text-[1.875rem] leading-[1.5]">
              {data.comparisonChip}
            </p>
          )}
          {data?.comparisonHeading?.length > 0 && (
            <div className="richtext richtext-center prose-headings:text-black prose-headings:font-geist prose-headings:font-[600] prose-headings:leading-none prose-headings:text-[4.5rem] prose-headings:tracking-[-0.225rem] lg:prose-headings:leading-[1.15] lg:prose-headings:text-[8rem] lg:prose-headings:tracking-[-0.4rem] w-[37.25rem] lg:w-[62.5rem] [--hl-tracking:-0.03em] lg:[--hl-dy:-0.55rem]">
              <PortableText
                value={data.comparisonHeading}
                components={measuredRichText}
              />
            </div>
          )}
        </div>

        {(columns.length > 0 || rows.length > 0) && (
          <>
            {/* Desktop: three columns */}
            <div className="hidden lg:grid grid-cols-3 gap-[3rem] h-[54.5rem]">
              {cards.map((card) => (
                <div
                  key={card.type}
                  className={`flex relative flex-col justify-between overflow-clip rounded-[0.75rem] px-[3rem] pt-[5.5rem] pb-[3rem] ${card.bgClass}`}
                >
                  {card.type === 'iq' && (
                    <>
                      <Image
                        src={statue}
                        alt=""
                        quality={100}
                        className="absolute top-[3.625rem] right-[2rem] pointer-events-none z-[5] w-[24.125rem]"
                      />
                      <Image
                        src={dotCards}
                        alt=""
                        quality={100}
                        className="object-cover absolute inset-0 w-full h-full pointer-events-none z-[1] opacity-[0.16] mix-blend-exclusion"
                      />
                    </>
                  )}
                  <div className={`flex relative z-[8] items-center min-h-[3.375rem] ${card.titleClass}`}>
                    {title(card)}
                  </div>
                  <div className="flex relative flex-col z-[8]">
                    {rows.map((row, i) => {
                      const content =
                        card.type === 'features'
                          ? row?.feature
                          : card.type === 'iq'
                            ? row?.values?.[0]?.text
                            : row?.values?.[1]?.text;
                      return (
                        <div
                          key={`${card.type}-row-${i}`}
                          className={`flex items-center font-geist leading-[1.12] h-[6.5rem] border-b last:border-b-0 ${card.textClass} ${card.dividerClass}`}
                        >
                          {content ?? ''}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile: tab toggle over one list */}
            <div className="flex flex-col lg:hidden gap-[1rem]">
              <div className="flex bg-black/[3%] border border-black/5 rounded-[0.75rem] gap-[0.5rem] p-[0.5rem]">
                {cards.slice(1).map((card) => {
                  const isActive = card.type === selectedTab;
                  return (
                    <button
                      key={card.type}
                      type="button"
                      onClick={() => setSelectedTab(card.type as 'iq' | 'other')}
                      className={`flex flex-1 justify-center items-center rounded-[0.5rem] px-[2rem] py-[1.875rem] transition-colors ${
                        isActive ? 'bg-[#003828] text-white' : 'text-black/40 opacity-50'
                      }`}
                    >
                      {card.column?.logo ? (
                        isActive ? (
                          <GroqImage
                            src={card.column.logo}
                            alt={card.column.label ?? ''}
                            style={{ width: '14.25rem' }}
                            className="!w-[14.25rem] h-auto"
                          />
                        ) : (
                          <Image src={logoAlt} alt="" className="pointer-events-none w-[14.25rem]" />
                        )
                      ) : (
                        card.column?.label && (
                          <span className="font-geist text-[2.5rem] font-[600] leading-[1.12] tracking-[-0.05rem]">
                            {card.column.label}
                          </span>
                        )
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-col border rounded-[0.375rem] bg-[#228E71]/5 border-black/5 gap-[2.75rem] px-[2rem] py-[1.75rem]">
                <div className="text-black">{title(cards[0])}</div>
                <div className="flex flex-col gap-[3.25rem]">
                  {rows.map((row, i) => {
                    const value = row?.values?.[selectedTab === 'iq' ? 0 : 1]?.text;
                    return (
                      <div
                        key={`m-row-${i}`}
                        className="flex items-center font-geist font-[500] text-[2rem] leading-[1.12] gap-[1.25rem]"
                      >
                        <span className="uppercase text-[#103037] shrink-0">{row?.feature ?? ''}</span>
                        <span aria-hidden className="flex-1 border-t border-dotted border-black/20 min-w-[1rem]" />
                        <span className="text-right text-black/50 shrink-0">{value ?? ''}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
